import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Shield, Search, FileWarning, MapPin, Info, Heart, Megaphone, Ban, HeartHandshake, Building2, AlertTriangle, Siren } from "lucide-react";
import InstallAppButton from "./InstallAppButton";

// Nav items rendered as react-router NavLinks. The Donate entry is rendered
// separately below as a plain in-page anchor to #donate-tip on the homepage —
// no /donate route change, just a scroll target.
const navItems = [
  { to: "/", label: "Registry", icon: Search, id: "nav-registry" },
  { to: "/urgent-rescues", label: "Urgent Rescues", icon: Siren, id: "nav-urgent-rescues" },
  { to: "/active-cases", label: "Active Cases", icon: AlertTriangle, id: "nav-active-cases" },
  { to: "/animal-ban-lookup", label: "Ban Lookup", icon: Ban, id: "nav-ban-lookup" },
  { to: "/rescue-partners", label: "Rescue Partners", icon: HeartHandshake, id: "nav-rescue-partners" },
  { to: "/local-shelters", label: "Shelters", icon: Building2, id: "nav-local-shelters" },
  { to: "/report", label: "Report Abuse", icon: FileWarning, id: "nav-report" },
  { to: "/nearby", label: "Find Help", icon: MapPin, id: "nav-nearby" },
  { to: "/advertise", label: "Advertise", icon: Megaphone, id: "nav-advertise" },
  { to: "/about", label: "About", icon: Info, id: "nav-about" },
];

// Donate link — plain <a> so the browser natively handles the #donate-tip
// anchor scroll on the homepage. On any other page, hitting `/#donate-tip`
// takes the visitor home + scrolls to the same Cash App / Zelle block.
const DONATE_HREF = "/#donate-tip";

export default function Navbar() {
  return (
    <header
      className="sticky top-0 z-40 bg-white border-b border-zinc-900"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-6">
          <Link
            to="/"
            className="flex items-center gap-3 group shrink-0"
            data-testid="brand-link"
          >
            <img
              src="/icon.svg"
              alt="Paws on Record"
              width="36"
              height="36"
              className="border border-zinc-900"
            />
            <div className="leading-tight">
              <div className="font-display font-black text-[15px] tracking-tight">
                PAWS ON RECORD
              </div>
              <div className="text-[10px] label-eyebrow">
                U.S. Abuser Registry
              </div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5 min-w-0">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.id}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-2 py-2 text-[11px] 2xl:text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-900 hover:bg-zinc-100"
                  }`
                }
              >
                <it.icon size={14} strokeWidth={2} />
                {it.label}
              </NavLink>
            ))}
            <a
              href={DONATE_HREF}
              data-testid="nav-donate"
              className="flex items-center gap-1.5 px-2 py-2 text-[11px] 2xl:text-xs font-semibold uppercase tracking-wider whitespace-nowrap text-zinc-900 hover:bg-zinc-100 transition-colors"
            >
              <Heart size={14} strokeWidth={2} />
              Donate
            </a>
          </nav>

          <div className="hidden sm:block xl:hidden shrink-0">
            <Link to="/report" className="btn-danger" data-testid="cta-report">
              <FileWarning size={14} /> Report
            </Link>
          </div>
          <InstallAppButton />
        </div>
      </div>
      {/* Mobile nav */}
      <div className="xl:hidden border-t border-zinc-200 overflow-x-auto">
        <div className="flex min-w-max">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`${it.id}-mobile`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap border-r border-zinc-200 ${
                  isActive ? "bg-zinc-900 text-white" : "text-zinc-900"
                }`
              }
            >
              <it.icon size={12} /> {it.label}
            </NavLink>
          ))}
          <a
            href={DONATE_HREF}
            data-testid="nav-donate-mobile"
            className="flex items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap border-r border-zinc-200 text-zinc-900"
          >
            <Heart size={12} /> Donate
          </a>
        </div>
      </div>
    </header>
  );
}
